/* eslint class-methods-use-this: 0, no-param-reassign: 0, no-async-promise-executor: 0 */
import { ExportFileTypes } from 'client/util/Constants';
import {
  cloneDeep,
  flatten,
  get,
  isEqual,
  union,
  uniq,
} from 'lodash';
import { quickPicksMetadata } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import ActivityLogFilterConsts from 'client/Insights/ActivityLog/constants/ActivityLogFilterConsts';
import DownloadUtil from 'client/util/DownloadUtil';
import EventMetadata, { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import InsightsEventsApi from 'client/api/v2/InsightsEventsApi';
import JobUtils from 'client/util/JobUtils';
import ModalEventBus from 'components/Modal/ModalEventBus';
import Vue from 'vue';
import moment from 'moment';

const { services } = ActivityLogFilterConsts;

// ----------------------------------------------------------------------------
// Private Helper Methods
// ----------------------------------------------------------------------------

const displayMaxExportWarning = () => {
  ModalEventBus.open({
    component: 'ConfirmationModal',
    componentProps: {
      bodyText: 'Only the first 100,000 rows were exported. Please use the API to export more.',
      cancelText: '',
      continueText: 'okay',
      headerText: 'Export Exceeded Maximum Limit',
    },
  });
};

/**
 * This function is used to create payload parameters for an `/events` API request.
 * Payload parameters include filtering by service, time range, and any other filters.
 * @param {Object} state - The current store state
 * @param {Boolean} options.includeFilters - Whether to include `searchTerm` in the request
 */
const getRequestOptions = (state, options = {}) => {
  const { includeFilters = true } = options;

  const serviceFilter = state.filters.find(d => d.field === eventFields.service);
  const service = serviceFilter ? serviceFilter.value : ['all'];

  const searchTerm = {};
  state.filters.forEach((filter) => {
    if (filter.field !== eventFields.service && filter.value.length) {
      if (!searchTerm.and) searchTerm.and = [];

      // Use the filter criteria specified in metadata or else directly filter by the path
      const metadatumValue = EventMetadata[filter.field];
      searchTerm.and.push(
        metadatumValue.filterCriteria
          ? metadatumValue.filterCriteria(filter.value)
          : { [metadatumValue.path]: filter.value },
      );
    }
  });

  return {
    service,
    start_time: state.startDate,
    ...((!state.quickPickDate && state.endDate) && {
      end_time: state.endDate,
    }),
    ...(includeFilters && Object.keys(searchTerm).length && { search_term: searchTerm }),
  };
};

/**
 * TODO: Tech-debt
 * This function is used to alter saved views data to use filters with a { field, value } structure
 * instead of the { field, values } structure that is being used in the database.
 * `value` was being used in so many places that simply changing this to use `value` was easiest.
 */
const fixViewDataStructure = (view) => {
  const newView = { ...view };
  if (newView.filters instanceof Array) {
    newView.filters = newView.filters
      .map(filter => ({ field: filter.field, value: filter.values }));
  }
  return newView;
};

// ----------------------------------------------------------------------------
// State Constants
// ----------------------------------------------------------------------------

const initialFilterOptionsState = {
  [eventFields.service]: [{ name: 'All', value: 'all', valueTextName: 'all' }].concat(services),
  [eventFields.eventType]: [],
  [eventFields.user]: [],
  [eventFields.system]: [],
};

const initialFilterState = [
  { field: eventFields.service, value: ['all'] },
  { field: eventFields.eventType, value: [] },
  { field: eventFields.user, value: [] },
  { field: eventFields.system, value: [] },
];

export class EventsModuleClass {
  extractModule() {
    return {
      namespaced: true,
      state: {
        cancelExport: false,
        endDate: null, // represented by Date object
        events: [],
        filterOptions: cloneDeep(initialFilterOptionsState),
        filters: cloneDeep(initialFilterState),
        eventTypeFound: [],
        initialFilterState: cloneDeep(initialFilterState),
        quickPickDate: null,
        savedViews: [],
        selectedView: null, // either an Object containing name, column, filters or null
        startDate: null, // represented by Date object
      },
      actions: {
        addFilter({ commit }, field) {
          if (!EventMetadata[field]) return;
          commit('ADD_FILTER', field);
        },

        addFilterOption({ commit }, filter) {
          commit('ADD_FILTER_OPTION', filter);
        },

        addToFilter({ commit }, { field, value }) {
          if (!EventMetadata[field]) return;
          commit('ADD_TO_FILTER', { field, value });
        },

        cancelExport({ commit }) {
          commit('SET_CANCEL_EXPORT', true);
        },

        clearFilters({ commit }) {
          commit('CLEAR_FILTERS');
        },

        clearFilterOption({ commit }, filterName) {
          commit('CLEAR_FILTER_OPTION', filterName);
        },

        createSavedView({ commit }, payload) {
          return InsightsEventsApi.createView(payload)
            .then((resp) => {
              const view = fixViewDataStructure(resp.data);
              commit('CREATE_SAVED_VIEW', view);
              return view;
            });
        },

        deleteSavedView({ commit }, { id }) {
          return InsightsEventsApi.deleteView(id)
            .then(() => {
              commit('DELETE_SAVED_VIEW', id);
            });
        },

        /**
         * Exports all Insights events data that satisfies the current filters chosen.
         * The progress modal will be shown for exports of more than 10k rows
         * Only exports a maximum of 1M rows
         */
        exportData({ commit, dispatch, state }, exportType = ExportFileTypes.json) {
          let data = [];
          const rowBatchSize = 10000;
          // eslint-disable-next-line max-len
          const totalNumRows = 1e5; // @todo Assume 1e5 rows until work is done to make this accurate
          const maxLimit = 1e5;
          let numRowsFetched = 0;

          const completeProgressCounter = () => {
            dispatch('ProgressModule/setCounter', { completed: totalNumRows }, { root: true });
          };

          const collectData = (searchAfter) => {
            const payload = { limit: rowBatchSize };
            if (searchAfter) payload.search_after = searchAfter;

            return dispatch('fetchEventsData', payload)
              .then(async (response) => {
                data = data.concat(response.data);
                numRowsFetched += response.resultCount;

                if (state.cancelExport) {
                  completeProgressCounter();
                  // Reset cancel export
                  commit('SET_CANCEL_EXPORT', false);
                  throw new Error('Events export cancelled');
                }

                // If response indicates there are no more rows to load, stop
                if (response.resultCount < response.limit) {
                  completeProgressCounter(); // This is to guarantee that progress modal completes
                  return;
                }

                // Stop fetching after hitting max limit
                if (numRowsFetched >= maxLimit) {
                  completeProgressCounter();
                  displayMaxExportWarning();
                  return;
                }

                // @todo Using a fake number of total rows, so fake completed count if it goes over
                const completed = (numRowsFetched >= totalNumRows)
                  ? totalNumRows - 9000
                  : numRowsFetched;
                dispatch('ProgressModule/setCounter', { completed, total: totalNumRows }, { root: true });

                // Continue to gather data while API indicates there are still more to load
                await collectData(response.searchAfter);
              });
          };

          return new Promise(async (resolve, reject) => {
            if (exportType !== ExportFileTypes.json && exportType !== ExportFileTypes.csv) {
              reject(new Error('Invalid file type'));
              return;
            }

            try {
              await collectData();
            } catch (err) {
              reject(err);
              return;
            }

            const startDate = moment(state.startDate).format('YYYYMMDD');
            const endDate = state.endDate
              ? moment(state.endDate).format('YYYYMMDD')
              : moment().format('YYYYMMDD');
            let fileName = `jcactivitylog_${startDate}`;
            if (endDate !== startDate) fileName += `_${endDate}`;

            if (exportType === ExportFileTypes.json) {
              DownloadUtil.downloadJson(data, `${fileName}.json`);
            } else if (exportType === ExportFileTypes.csv) {
              const csv = JobUtils.convertJsonToCsv(data, {
                fieldsToInclude: ['timestamp', 'event_type', 'service'],
              });
              DownloadUtil.downloadFile(csv, `${fileName}.csv`);
            }
            resolve(numRowsFetched);
          });
        },

        /**
         * Fetches all distinct values for a given event field within the provided time range
         * @param [String] payload.field - The event field to return distinct values for
         */
        fetchDistinctValues({ dispatch, state }, { field }) {
          // The list of services are currently hardcoded and already defined
          if (field === eventFields.service) return Promise.resolve();

          let tasks = [];
          const { path, paths } = EventMetadata[field];
          if (path) {
            const requestOptions = getRequestOptions(state, { includeFilters: false });

            tasks = [InsightsEventsApi.fetchDistinct({
              ...requestOptions,
              field: path,
            })];
          } else if (paths) {
            // If the event field has more than one possible value path, perform multiple querys
            tasks = paths.map((pathObj) => {
              const options = getRequestOptions(state, { includeFilters: false });

              // If the path is only valid with a filter, add that filter to the query
              if (pathObj.filter) {
                if (!options.search_term) options.search_term = { and: [] };
                options.search_term.and.push(pathObj.filter);
              }

              return InsightsEventsApi.fetchDistinct({
                ...options,
                field: pathObj.path,
              });
            });
          }

          return Promise.allSettled(tasks).then((responses) => {
            // If there are more filter options than retrieved, throw error
            if (responses.some(resp => get(resp, 'value.data.sum_other_doc_count'))) {
              throw new Error('Too many options to filter (> 10k)');
            }

            const buckets = flatten(responses.map(resp => get(resp, 'value.data.buckets', [])));
            const optionValues = buckets.map(d => d.key_as_string || String(d.key));
            const options = uniq(optionValues)
              .sort()
              .map((element) => {
                const option = {
                  name: element,
                  value: element,
                };
                if (option.name === 'mac os x') {
                  option.name = 'macOS';
                }
                return option;
              });
            dispatch('setFilterOptions', { filterName: field, options });
            return options;
          }).catch((err) => {
            dispatch('clearFilterOption', field);
            throw new Error(err.message);
          });
        },

        fetchWidgetData({ dispatch }, { field }) {
          return InsightsEventsApi.fetchMultiDistinct(field)
            .then(resp => ({
              results: resp.data,
            })).catch((err) => {
              dispatch('clearFilterOption', field);
              throw new Error(err.message);
            });
        },

        fetchEventsCount({ state }, payload) {
          return InsightsEventsApi.fetchCount({
            ...getRequestOptions(state),
            ...payload,
          });
        },

        fetchEventsData({ state, commit }, payload) {
          return InsightsEventsApi.fetch({
            sort: 'DESC',
            ...getRequestOptions(state),
            ...payload,
          })
            .then((response) => {
              const searchAfter = response.headers['x-search_after'];
              commit('SET_EVENTS', response.data);
              return {
                data: response.data,
                limit: +response.headers['x-limit'],
                resultCount: +response.headers['x-result-count'],
                searchAfter: searchAfter ? JSON.parse(searchAfter) : '',
              };
            });
        },

        fetchEventsIntervalCount({ state }, payload) {
          return InsightsEventsApi.fetchIntervalCount({
            ...getRequestOptions(state),
            timezone: moment().format('ZZ'),
            ...payload,
          });
        },

        fetchSavedViews({ commit }) {
          return InsightsEventsApi.fetchViews()
            .then((resp) => {
              const views = resp.data.map(view => fixViewDataStructure(view));
              commit('SET_SAVED_VIEWS', views);
              return views;
            });
        },

        modifySavedView({ commit }, { id, view }) {
          return InsightsEventsApi.updateView(id, view)
            .then((resp) => {
              const newView = fixViewDataStructure(resp.data);
              commit('MODIFY_SAVED_VIEW', { id, view: newView });
              return newView;
            });
        },

        refreshDates({ commit, state }) {
          if (state.quickPickDate) {
            const startTime = quickPicksMetadata[state.quickPickDate].startDate();
            const endTime = moment().toDate();

            commit('SET_START_DATE', startTime);
            commit('SET_END_DATE', endTime);
          }
        },

        removeFilter({ commit }, field) {
          commit('REMOVE_FILTER', field);
        },

        setEndDate({ commit }, date) {
          commit('SET_END_DATE', date);
        },

        setFilter({ commit }, { field, value }) {
          commit('SET_FILTER', { field, value });
        },

        setFilterOptions({ commit }, { filterName, options }) {
          commit('SET_FILTER_OPTIONS', { filterName, options });
        },

        setEventTypeFound({ commit }, { filter, reset }) {
          if (reset) {
            commit('RESET_EVENT_TYPE_FOUND');
          } else {
            commit('SET_EVENT_TYPE_FOUND', filter);
          }
        },

        setQuickPickDate({ commit }, date) {
          commit('SET_QUICK_PICK_DATE', date);
        },

        setSelectedView({ commit }, view) {
          commit('SET_SELECTED_VIEW', view);
        },

        setStartDate({ commit }, date) {
          commit('SET_START_DATE', date);
        },
      },

      getters: {
        areFiltersCleared(state) {
          return isEqual(state.filters, initialFilterState);
        },

        getEvents(state) {
          return state.events;
        },

        getEventTypeFound(state) {
          return state.eventTypeFound;
        },
      },

      mutations: {
        ADD_FILTER(state, field) {
          state.filters.push({ field, value: [] });
        },

        ADD_FILTER_OPTION(state, filter) {
          // Set the options list to be an empty array; use set to make it reactive
          Vue.set(state.filterOptions, filter, []);
        },

        ADD_TO_FILTER(state, { field, value }) {
          // Adds the provided value to the filter value
          const filters = cloneDeep(state.filters);
          if (!(value instanceof Array)) value = [value]; // convert to Array if single value
          const filter = filters.find(d => d.field === field);
          if (filter) {
            filter.value = union(filter.value, value);
          } else {
            filters.push({ field, value });
          }
          Vue.set(state, 'filters', filters);
        },

        CLEAR_FILTERS(state) {
          state.filters = cloneDeep(initialFilterState);
        },

        CLEAR_FILTER_OPTION(state, filterName) {
          state.filterOptions[filterName] = [];
        },

        CREATE_SAVED_VIEW(state, view) {
          const newSavedViews = [...state.savedViews];
          newSavedViews.push(view);
          Vue.set(state, 'savedViews', newSavedViews);
        },

        DELETE_SAVED_VIEW(state, id) {
          const newSavedViews = [...state.savedViews];
          const viewIndex = newSavedViews.findIndex(v => v.id === id);
          if (viewIndex > -1) {
            newSavedViews.splice(viewIndex, 1);
            Vue.set(state, 'savedViews', newSavedViews);
          }
        },

        MODIFY_SAVED_VIEW(state, { id, view }) {
          const viewIndex = state.savedViews.findIndex(v => v.id === id);
          if (viewIndex > -1) {
            Vue.set(state.savedViews, viewIndex, view);
          }
        },

        REMOVE_FILTER(state, field) {
          const index = state.filters.findIndex(d => d.field === field);
          if (index > -1) {
            state.filters.splice(index, 1);
          }
        },

        RESET_EVENT_TYPE_FOUND(state) {
          state.eventTypeFound = [];
        },

        SET_CANCEL_EXPORT(state, cancelExport) {
          state.cancelExport = cancelExport;
        },

        SET_END_DATE(state, date) {
          state.endDate = date;
        },

        SET_EVENTS(state, events) {
          state.events = events;
        },

        SET_FILTER(state, { field, value }) {
          // Replaces entirely the filter value with the provided value
          const filters = cloneDeep(state.filters);
          const filter = filters.find(d => d.field === field);
          if (filter) {
            filter.value = value;
          } else {
            filters.push({ field, value });
          }
          Vue.set(state, 'filters', filters);
        },

        SET_FILTER_OPTIONS(state, { filterName, options }) {
          Vue.set(state.filterOptions, filterName, options);
        },

        SET_EVENT_TYPE_FOUND(state, filter) {
          state.eventTypeFound.push(filter);
        },

        SET_QUICK_PICK_DATE(state, date) {
          state.quickPickDate = date;
        },

        SET_SAVED_VIEWS(state, views) {
          state.savedViews = views;
        },

        SET_SELECTED_VIEW(state, view) {
          state.selectedView = view;
        },

        SET_START_DATE(state, date) {
          state.startDate = date;
        },
      },
    };
  }
}

export function createEventsModule() {
  return new EventsModuleClass().extractModule();
}

export default createEventsModule();
